import React, { createContext, useContext, useCallback, useEffect } from 'react';
import { useUserStats } from '../hooks/useUserStats';
import { useCalendarEvents } from '../hooks/useCalendarEvents';
import { useUserProfile } from '../hooks/useUserProfile';
import { useAuth } from './AuthProvider';
import { formatMonthForAPI } from '../api';

const UserDataContext = createContext(null);

export const UserDataProvider = ({ children }) => {
  const { isAuthenticated, token } = useAuth();
  
  // Initialize with current month
  const currentMonth = formatMonthForAPI();
  
  // Use our custom hooks
  const statsHook = useUserStats(currentMonth);
  const calendarHook = useCalendarEvents(currentMonth);
  const profileHook = useUserProfile();

  // Sync month changes between stats and calendar
  const changeMonth = useCallback((newMonth) => {
    statsHook.changeMonth(newMonth);
    calendarHook.changeMonth(newMonth);
  }, [statsHook.changeMonth, calendarHook.changeMonth]);

  const navigateMonth = useCallback((direction) => {
    statsHook.navigateMonth(direction);
    calendarHook.navigateMonth(direction);
  }, [statsHook.navigateMonth, calendarHook.navigateMonth]);

  const goToToday = useCallback(() => {
    const today = formatMonthForAPI();
    changeMonth(today);
  }, [changeMonth]);

  // Refresh all data
  const refreshAllData = useCallback(async () => {
    await Promise.all([
      statsHook.refreshStats(),
      calendarHook.refreshEvents(),
      profileHook.refreshProfile()
    ]);
  }, [statsHook.refreshStats, calendarHook.refreshEvents, profileHook.refreshProfile]);

  // Clear all data (useful for logout)
  const clearAllData = useCallback(() => {
    calendarHook.clearCache();
    profileHook.clearProfile();
  }, [calendarHook.clearCache, profileHook.clearProfile]);

  // Clear data when user logs out
  useEffect(() => {
    if (!isAuthenticated || !token) {
      clearAllData();
    }
  }, [isAuthenticated, token, clearAllData]);

  // Combined loading state
  const isLoading = statsHook.loading || calendarHook.loading || profileHook.loading;
  
  // Combined error state
  const hasError = !!(statsHook.error || calendarHook.error || profileHook.error);
  const errors = {
    stats: statsHook.error,
    calendar: calendarHook.error,
    profile: profileHook.error
  };

  // Month display helper
  const getMonthDisplayName = useCallback((month = statsHook.currentMonth) => {
    try {
      const date = new Date(month + '-01');
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long' 
      });
    } catch {
      return 'Current Month';
    }
  }, [statsHook.currentMonth]);

  // Legacy compatibility - format data like the old user data structure
  const getLegacyUserData = useCallback(() => {
    if (!profileHook.profile) return null;

    return {
      ...profileHook.profile,
      posts_cal_events: calendarHook.events,
      stats: statsHook.stats
    };
  }, [profileHook.profile, calendarHook.events, statsHook.stats]);

  const contextValue = {
    // Individual hook data
    stats: {
      data: statsHook.stats,
      loading: statsHook.loading,
      error: statsHook.error,
      refresh: statsHook.refreshStats,
      retry: statsHook.retry
    },
    calendar: {
      events: calendarHook.events,
      loading: calendarHook.loading,
      error: calendarHook.error,
      refresh: calendarHook.refreshEvents,
      retry: calendarHook.retry,
      getEventById: calendarHook.getEventById,
      getEventsByDate: calendarHook.getEventsByDate,
      eventsCount: calendarHook.eventsCount
    },
    profile: {
      data: profileHook.profile,
      loading: profileHook.loading,
      error: profileHook.error,
      refresh: profileHook.refreshProfile,
      retry: profileHook.retry,
      updateLocally: profileHook.updateProfileLocally,
      // Convenience getters
      subscriptionStatus: profileHook.subscriptionStatus,
      subscriptionPlan: profileHook.subscriptionPlan,
      socialAccounts: profileHook.socialAccounts,
      timezone: profileHook.timezone,
      isOnboardingComplete: profileHook.isOnboardingComplete
    },
    
    // Month navigation
    currentMonth: statsHook.currentMonth,
    monthDisplayName: getMonthDisplayName(),
    changeMonth,
    navigateMonth,
    goToToday,
    
    // Global actions
    refreshAllData,
    clearAllData,
    
    // Global state
    isLoading,
    hasError,
    errors,
    
    // Legacy compatibility
    legacyUserData: getLegacyUserData(),
    
    // Utility functions
    formatMonthForAPI,
    getMonthDisplayName
  };

  return (
    <UserDataContext.Provider value={contextValue}>
      {children}
    </UserDataContext.Provider>
  );
};

export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
};

// Convenience hooks for specific data types
export const useStats = () => {
  const { stats } = useUserData();
  return stats;
};

export const useCalendar = () => {
  const { calendar } = useUserData();
  return calendar;
};

export const useProfile = () => {
  const { profile } = useUserData();
  return profile;
}; 